import React, {MouseEvent} from 'react';
import {
  PopoverProps as MuiPopoverProps,
  Popover,
  Stack,
  Box,
  styled,
} from '@mui/material';
import {
  Body,
  designSystemToken,
  Icon,
  IconName,
  Checkbox,
} from '@lightricks/react-design-system';
import {scrollbarMixins} from '@contentlab/styles/mixins';

const checkboxCheckedSrc = '/assets/svg/checkbox-checked.svg';
const checkboxUncheckedSrc = '/assets/svg/checkbox-unchecked.svg';

export type MenuOption = {
  id: string;
  title: string;
  icon?: IconName;
  onClick: () => void;
  isDisabled?: boolean;
};

interface ActionsMenuProps {
  menuOpen: boolean;
  onCloseMenu: (event: MouseEvent<HTMLDivElement>) => void;
  anchorEl: HTMLElement | null;
  menuOptions: MenuOption[];
  anchorOrigin?: MuiPopoverProps['anchorOrigin'];
  selectedOptionId?: string | string[] | null;
  multiple?: boolean;
}

const CheckboxContainer = styled(Box)(() => ({
  'width': '20px',
  'height': '24px',
  '.MuiFormHelperText-root': {display: 'none'},
}));

function ActionsMenu({
  menuOpen,
  onCloseMenu,
  anchorEl,
  menuOptions,
  anchorOrigin = {vertical: 'center', horizontal: 'right'},
  selectedOptionId,
  multiple = false,
}: ActionsMenuProps) {
  const isOptionSelected = (optionId: string) => {
    if (multiple && Array.isArray(selectedOptionId)) {
      return selectedOptionId.includes(optionId);
    }
    return selectedOptionId === optionId;
  };

  return (
    <Popover
      slotProps={{
        paper: {
          sx: {
            borderRadius: '24px',
            padding: '8px',
            ...scrollbarMixins.overrideScrollbarHidden(),
          },
        },
      }}
      open={menuOpen}
      onClose={onCloseMenu}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
    >
      <Stack gap="4px">
        {menuOptions.map((option) => (
          <Stack
            key={option.title}
            direction="row"
            gap="12px"
            padding="4px 8px"
            height="36px"
            alignItems="center"
            onClick={(event) => {
              if (option.isDisabled) {
                return;
              }
              if (!multiple) {
                onCloseMenu(event);
              }
              option.onClick();
            }}
            sx={{
              'cursor': option.isDisabled ? 'not-allowed' : 'pointer',
              'borderRadius': '12px',
              'background': isOptionSelected(option.id)
                ? designSystemToken('semantic.bg.tertiary')
                : 'unset',
              ':hover': {
                background: 'rgba(44, 45, 48, 0.04)',
              },
            }}
          >
            {multiple && (
              <CheckboxContainer>
                <Checkbox
                  checked={isOptionSelected(option.id)}
                  onChange={() => {}}
                  customCheckedIcon={<img src={checkboxCheckedSrc} alt="" />}
                  customUncheckedIcon={
                    <img src={checkboxUncheckedSrc} alt="" />
                  }
                />
              </CheckboxContainer>
            )}
            {!multiple && option.icon && (
              <Icon
                name={option.icon}
                size="medium"
                appearance="neutral"
                color={designSystemToken('semantic.fg.primary')}
              />
            )}
            <Body size="lg" color={designSystemToken('semantic.fg.primary')}>
              {option.title}
            </Body>
          </Stack>
        ))}
      </Stack>
    </Popover>
  );
}

export default ActionsMenu;
